import { useEffect } from 'react';
import { measureAndLog } from '@hotjar/performance';
import styled from '@emotion/styled';

import {
  useNavigation,
  LEGACY_NARROW_SCREEN_MAX_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH as SIDEBAR_MINIMIZED_WIDTH,
  SIDEBAR_WIDTH as SIDEBAR_EXPANDED_WIDTH,
} from '@hotjar/navigation';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { css } from '@emotion/react';
import { MARKS } from '@hotjar/webapp/performance';
import * as AllRoutes from '@hotjar/webapp/constants/routes';
import type { Routes } from '@hotjar/navigation/src/types';
import { Nav } from '@hotjar/webapp/features/Core/Nav';
import { useUIState } from '@hotjar/webapp/features/Core/hooks/useUIState';
import { getLoggedIn } from '@hotjar/webapp/domains/common/user-data';
import { MaintenancePage } from '@hotjar/insights-common/components/MaintenancePage';
import { useCurrentSiteId } from '@hotjar/site';

import { validateAndProcessUtmParams } from '@hotjar/webapp/utils/utm-params';
import { AppRoutes } from './AppRoutes';
import { GlobalRuntimeHooks } from './components/GlobalRuntimeHooks';
import { Overlays } from './components/Overlays';
import { useMaintenanceMode } from './hooks/useMaintenanceMode';

const AppContent = styled.div<{
  isSidebarMinimized: boolean;
  isSidebarHidden: boolean;
  isAppInterfaceVisible: boolean;
  sidebarWidth: number;
  sidebarMinimizedWidth: number;
}>`
  position: relative;

  ${({ isSidebarHidden }) =>
    !isSidebarHidden &&
    css`
      transition: margin-left 300ms ease-in-out, width 300ms ease-in-out;
    `};

  ${({ isSidebarMinimized, isSidebarHidden, sidebarWidth, sidebarMinimizedWidth }) => {
    if (isSidebarHidden) {
      return `
      width: 100%;
      margin-left: 0;
      `;
    }

    const offset = isSidebarMinimized ? sidebarMinimizedWidth : sidebarWidth;

    return `
    margin-left: ${offset}px;
    width: calc(100% - ${offset}px);
  `;
  }};

  /* This is to match what the main content container does */
  @media (max-width: ${LEGACY_NARROW_SCREEN_MAX_WIDTH}px) {
    ${({ isAppInterfaceVisible, isSidebarHidden, sidebarMinimizedWidth }) =>
      !isAppInterfaceVisible || isSidebarHidden
        ? `
      width: 100%;
      margin-left: 0;
      `
        : `
      width: calc(100% - ${sidebarMinimizedWidth}px);
      margin-left: ${sidebarMinimizedWidth}px;
      `};
  }

  ${({ isAppInterfaceVisible }) =>
    !isAppInterfaceVisible &&
    `
      margin-left: 0;
      width: 100%;
  `}
`;

export const App = (): JSX.Element => {
  const isInMaintenanceMode = useMaintenanceMode();
  const currentSiteId = useCurrentSiteId();

  const { initializeRoutes, isSidebarMinimized, isSidebarHidden } = useNavigation();

  const { isAppInterfaceVisible } = useUIState();

  const isLoggedIn = getLoggedIn();

  useEffect(() => {
    const routes = AllRoutes as unknown as Routes;

    initializeRoutes(routes, getRoute);
  }, [initializeRoutes]);

  useEffect(() => {
    validateAndProcessUtmParams();
  }, []);

  useEffect(() => {
    // Perf: Mark application root render timings
    measureAndLog({
      name: MARKS.APP_RENDERED,
      startMark: MARKS.APP_PROVIDERS_RENDERED,
      addMark: true,
    });
  }, []);

  if (isInMaintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <div id="app-container">
      <GlobalRuntimeHooks />

      {isAppInterfaceVisible && isLoggedIn && <Nav />}

      <AppContent
        isSidebarMinimized={isSidebarMinimized}
        isSidebarHidden={isSidebarHidden}
        isAppInterfaceVisible={isAppInterfaceVisible}
        sidebarWidth={SIDEBAR_EXPANDED_WIDTH}
        sidebarMinimizedWidth={SIDEBAR_MINIMIZED_WIDTH}
      >
        <AppRoutes currentSiteId={currentSiteId} />
      </AppContent>

      <Overlays isAppInterfaceVisible={isAppInterfaceVisible} isLoggedIn={isLoggedIn} />
    </div>
  );
};
